import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Info } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Progress } from "@/components/ui/progress";
import { useCart } from "@/context/CartContext";
import { gbp, imgSrc } from "@/lib/api";
import { useQuote } from "@/lib/useQuote";
import { useMeta } from "@/lib/useMeta";

export default function CartSheet() {
  const { items, setQty, subtotal, open, setOpen } = useCart();
  const nav = useNavigate();
  const quote = useQuote(items);
  const meta = useMeta();
  const FREE_OVER = meta?.free_over;
  const left = Math.max(0, (FREE_OVER ?? 0) - subtotal);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md bg-[#FDFBF7] flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl">Your basket</SheetTitle>
        </SheetHeader>
        {items.length === 0 ? (
          <div className="flex-1 grid place-items-center text-slate-500 text-sm" data-testid="cart-empty">Your basket is empty.</div>
        ) : (
          <>
            {FREE_OVER != null && (
              <div className="mt-2 rounded-xl bg-white border border-[#EDE4D3] p-4">
                <p className="text-sm" data-testid="free-delivery-progress-text">
                  {left > 0 ? <>Add <b>{gbp(left)}</b> more for free Evri Standard delivery</> : <b className="text-emerald-700">You've unlocked free Evri Standard delivery</b>}
                </p>
                <Progress value={Math.min(100, (subtotal / FREE_OVER) * 100)} className="mt-3 h-2" />
              </div>
            )}
            {quote?.error && <p className="mt-2 rounded-xl bg-rose-50 text-rose-800 text-sm p-3" data-testid="cart-stock-error">{quote.error}</p>}
            <div className="flex-1 overflow-y-auto divide-y divide-[#EDE4D3] mt-2">
              {items.map((i) => (
                <div key={i.id} className="py-4 flex gap-4" data-testid={`cart-item-${i.id}`}>
                  <img src={imgSrc(i.image_url)} alt={i.name} className="w-16 h-16 object-contain bg-white rounded-lg border border-[#EDE4D3] p-1" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-snug line-clamp-2">{i.name}</p>
                    {i.contents && <p className="text-xs text-slate-500 leading-snug mt-0.5" data-testid={`cart-hamper-contents-${i.id}`}>{i.contents.join(" · ")}</p>}
                    <p className="text-xs text-slate-500">{i.store}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <button onClick={() => setQty(i.id, i.qty - 1)} className="w-7 h-7 rounded-full border grid place-items-center hover:bg-white" data-testid={`cart-decrease-${i.id}`}><Minus className="w-3 h-3" /></button>
                      <span className="text-sm w-5 text-center" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                      <button onClick={() => setQty(i.id, i.qty + 1)} className="w-7 h-7 rounded-full border grid place-items-center hover:bg-white" data-testid={`cart-increase-${i.id}`}><Plus className="w-3 h-3" /></button>
                      <button onClick={() => setQty(i.id, 0)} className="ml-auto text-slate-400 hover:text-[#8C2220]" data-testid={`cart-remove-${i.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <p className="text-sm font-semibold">{gbp(i.price * i.qty)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-[#EDE4D3] pt-4 space-y-3">
              <div className="flex justify-between text-base"><span>Subtotal</span><b data-testid="cart-subtotal">{gbp(subtotal)}</b></div>
              {quote?.options && (
                <div className="flex justify-between text-sm text-slate-600" data-testid="cart-postage-estimate">
                  <span>Evri Standard · {quote.weight_kg}kg</span>
                  <span>{quote.options[0].fee === 0 ? "FREE" : gbp(quote.options[0].fee)}</span>
                </div>
              )}
              <p className="text-xs text-slate-500 flex gap-2"><Info className="w-4 h-4 shrink-0" />We buy your items fresh from local stores. If anything is out of stock, we send the rest and refund the missing item.</p>
              <button
                onClick={() => { setOpen(false); nav("/checkout"); }}
                className="w-full rounded-full bg-[#8C2220] text-white py-3.5 font-semibold hover:bg-[#701A18] active:scale-[0.98] transition-[background-color,transform]"
                data-testid="cart-checkout-button"
              >
                Checkout
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
